<template>
    <component
        :is="type === 'link' ? 'a' : 'button'"
        :id="id"
        :class="getButtonClasses"
        :disabled="isDisabled"
        :type="type"
        class="transition-colors duration-100 flex items-center justify-center font-semibold text-center relative gap-4"
        @click="handleClick"
    >
        <template v-if="!loading">
            <template v-if="look !== 'icon'">
                <svg-icon
                    v-if="icon && iconPosition === 'left'"
                    :type="icon"
                />
                <span v-if="$slots.default" :class="[uppercase ? 'uppercase' : '', !lowercase ? 'first-letter:uppercase' : '', 'leading-normal']">
                    <slot></slot>
                </span>
                <svg-icon
                    v-if="icon && iconPosition === 'right'"
                    :type="icon"
                />
            </template>
            <template v-else>
                <svg-icon :type="icon"
                />
            </template>
        </template>
        <template v-else>
            <template v-if="!$slots['replace-loading']">
                 <span v-if="$slots.default" :class="[uppercase ? 'uppercase' : '', !lowercase ? 'first-letter:uppercase' : '']">
                    <slot></slot>
                </span>
                <loading
                    size="small"
                    type="spinner"
                    class="ml-4 mix-blend-multiply"
                />
            </template>
            <slot v-else name="replace-loading"/>
        </template>
    </component>
</template>

<script>
import SvgIcon from './SvgIcon.vue';
import Loading from "./Loading.vue";

export default {
    name: 'simple-button',
    components: {
        Loading,
        'svg-icon': SvgIcon,
    },
    props: {
        display: {
            type: String,
            default: 'primary',
            validator: value => [
                'primary',
                'secondary',
                'success',
                'info',
                'warning',
                'danger',
                'neutral',
            ].includes(value),
        },
        look: {
            type: String,
            default: 'secondary',
            validator: value => [
                'primary',
                'secondary',
                'text',
                'icon',
            ].includes(value)
        },
        disabled: Boolean,
        loading: Boolean,
        uppercase: Boolean,
        lowercase: Boolean,
        type: {
            type: String,
            default: 'button',
        },
        icon: String,
        size: {
            type: String,
            default: 'medium',
            validator: value => ['medium', 'small'].includes(value),
        },
        small: Boolean,
        whiteBackground: Boolean,
        overrideDisabledClass: String,
        id: String,
        iconPosition: {
            type: String,
            default: 'right',
            validator: value => ['left', 'right'].includes(value),
        },
        autoWidth: Boolean,
    },
    data: function () {
        return {
            buttonClasses: {
                primary: 'rounded-32 ',
                secondary: 'rounded-32 border ',
                text: '',
                icon: 'rounded-32 ',
            },
            enabledClasses: {
                primary: {
                    'primary': 'bg-primary hover:bg-primary-80 text-white',
                    'secondary': 'bg-secondary hover:bg-secondary-80 text-white',
                    'success': 'bg-semantic-success hover:bg-semantic-success-dark text-white',
                    'info': 'bg-semantic-info hover:bg-semantic-info-dark text-white',
                    'warning': 'bg-semantic-warning hover:bg-semantic-warning-dark text-white',
                    'danger': 'bg-semantic-error hover:bg-semantic-error-dark text-white',
                    'primary-danger': 'bg-primary hover:bg-semantic-error text-white',
                    'neutral': 'bg-primary-20 hover:bg-primary text-primary-60 hover:text-white',
                },
                secondary: {
                    'primary': 'border-primary text-primary hover:bg-primary hover:text-white',
                    'secondary': 'border-secondary text-secondary hover:bg-secondary hover:text-white',
                    'success': 'border-semantic-success text-semantic-success hover:bg-semantic-success hover:text-white',
                    'info': 'border-semantic-info text-semantic-info hover:bg-semantic-info hover:text-white',
                    'warning': 'border-semantic-warning text-semantic-warning hover:bg-semantic-warning hover:text-white',
                    'danger': 'border-semantic-error text-semantic-error hover:bg-semantic-error hover:text-white',
                    'primary-danger': 'border-primary text-primary hover:border-semantic-error hover:bg-semantic-error hover:text-white',
                    'neutral': 'border-primary-20 text-primary hover:bg-primary-40 hover:text-primary-80',
                },
                text: {
                    'primary': 'text-primary hover:underline',
                    'secondary': 'text-secondary hover:underline',
                    'success': 'text-semantic-success hover:underline',
                    'info': 'text-semantic-info hover:underline',
                    'warning': 'text-semantic-warning hover:underline',
                    'danger': 'text-semantic-error hover:underline',
                    'primary-danger': 'text-primary hover:text-semantic-error hover:underline',
                    'neutral': 'text-primary-60 hover:text-primary hover:underline',
                },
                icon: {
                    'primary': 'bg-primary hover:bg-primary-80 text-white',
                    'secondary': 'bg-secondary hover:bg-secondary-80 text-white',
                    'success': 'bg-semantic-success hover:bg-opacity-80 text-white',
                    'info': 'bg-semantic-info hover:bg-opacity-80 text-white',
                    'warning': 'bg-semantic-warning hover:bg-opacity-80 text-white',
                    'danger': 'bg-semantic-error hover:bg-opacity-80 text-white',
                    'primary-danger': 'bg-primary hover:bg-semantic-error text-white',
                    'neutral': 'bg-primary-20 hover:bg-primary text-primary-100 hover:text-white',
                },
            },
            disabledClasses: {
                primary: 'bg-primary-20 text-primary-60',
                secondary: 'border-primary-20 text-primary-60',
                text: 'text-primary-60',
                icon: 'bg-primary-20 text-primary-60 text-white',
            },
        };
    },
    computed: {
        buttonClass() {
            return this.buttonClasses[this.look];
        },
        enabledClass() {
            return this.enabledClasses[this.look][this.display];
        },
        disabledClass() {
            if (this.overrideDisabledClass) {
                return this.overrideDisabledClass;
            }

            return this.disabledClasses[this.look];
        },
        isDisabled() {
            return this.disabled || this.loading;
        },
        getButtonClasses() {
            let classes = [this.buttonClass, 'text-body-sm'];

            if (this.isDisabled) {
                classes.push('cursor-not-allowed pointer-events-none', this.disabledClass);
            } else {
                classes.push('cursor-pointer', this.enabledClass);
                if (this.look === 'secondary' && this.whiteBackground) {
                    classes.push('bg-white');
                }
            }

            if (!['text'].includes(this.look)) {
                if (!this.small && this.size === 'medium') {
                    classes.push('h-44');
                    if (this.look === 'icon') {
                        classes.push('w-44');
                    }
                } else {
                    classes.push('h-36');
                    if (this.look === 'icon') {
                        classes.push('w-36');
                    }
                }
                if (!this.autoWidth && this.look !== 'icon') {
                    classes.push('w-fit');
                }
                if (this.$slots.default) {
                    classes.push('px-16 py-8')
                } else {
                    classes.push('p-8')
                }
            }

            return classes;
        }
    },
    methods: {
        handleClick() {
            this.$emit('click');
        },
    },
};
</script>
